<script setup>
	import { ref, computed } from 'vue';

	import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
	import { useClientsStore } from '../stores/clients';
	import { useUserStore } from '../stores/user';

	const uri = import.meta.env.VITE_URL;
	const fallbackImage = `${uri}/storage/img/cli/vide.webp`;
	const clientsStore = useClientsStore();
	const people = ref(null);
	
	const count = ref(0);
    const pageNumber = ref(1);
	const currentPage = ref(1);
    const totalPages = ref(1);
	const userStore = useUserStore();

	const fetchNewClients = (page = 1) => {
		pageNumber.value = page
		clientsStore.getLastClients(page)
		.then(res => 
		{
			count.value = res.count;
			totalPages.value = Math.ceil(count.value / 100)
			people.value = res.clients
		})
		.catch(err => console.error(err))
	}
	fetchNewClients()

	function handleImageError(event) {
        event.target.src = fallbackImage;
	}

	function calculateAge(dateString) {
		let today = new Date();
		let birthDate = new Date(dateString);
		let age = today.getFullYear() - birthDate.getFullYear();
		let monthDifference = today.getMonth() - birthDate.getMonth();
		if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}
		return age;
	}

    const pages = computed(() => {
        const range = 2;
        const pageList = [];
        
        if (totalPages.value <= 7) {
            for (let i = 1; i <= totalPages.value; i++) {
                pageList.push(i);
            }
        } else {
            if (currentPage.value <= range + 2) {
                for (let i = 1; i <= range + 2; i++) {
                    pageList.push(i);
                }
                pageList.push('...');
                pageList.push(totalPages.value);
            } else if (currentPage.value >= totalPages.value - (range + 1)) {
                pageList.push(1);
                pageList.push('...');
                for (let i = totalPages.value - (range + 2); i <= totalPages.value; i++) {
                    pageList.push(i);
                }
            } else {
                pageList.push(1);
                pageList.push('...');
                for (let i = currentPage.value - range; i <= currentPage.value + range; i++) {
                    pageList.push(i);
                }
                
                pageList.push('...');
                pageList.push(totalPages.value);
            }
        }
        return pageList;
    });

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages.value) {
            currentPage.value = page;
			fetchNewClients(currentPage.value)
        }
    };

    const previousPage = () => {
        if (currentPage.value > 1) {
            currentPage.value -= 1;
            switch (fetchType.value) {
                case 'commonChoices':
                    fetchAllCommonChoices(currentPage.value);
                    break;
                case 'allChoices':
                    fetchAllChoices(currentPage.value);
                    break;
                case 'propositions':
                    fetchAllPropositions(currentPage.value);
                    break;
            }
        }
    };

    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value += 1;
            switch (fetchType.value) {
                case 'commonChoices':
                    fetchAllCommonChoices(currentPage.value);
                    break;
                case 'allChoices':
                    fetchAllChoices(currentPage.value);
                    break;
                case 'propositions':
                    fetchAllPropositions(currentPage.value);
                    break;
            }
        }
    };

	const isFromAgence = (client) => {
		if(userStore.userLog.adm_util == 1 && client.visu_cli === 'admin'){
            return {name: client.pNoms_cli + ' ' + client.nom_cli, can: true};
        }

        if(client.visu_cli === 'conseiller'){
            if(userStore.userLog.agences.includes(client.idAgence_cli)){
                return {name: client.pNoms_cli + ' ' + client.nom_cli, can: true};
            } else {
				if (client.nom_cli) {
					let formattedNomCli = client.nom_cli.substring(0, 3)
						+ '*'.repeat(Math.max(client.nom_cli.length - 3, 0));
                    return {name: client.pNoms_cli + ' ' + formattedNomCli, can: false};
				}
            }    
        }

        if(client.visu_cli === 'public'){
            return {name: client.pNoms_cli + ' ' + client.nom_cli, can: true};
        } else {
			return {name: client.pNoms_cli + ' ' + client.nom_cli, can: true};
		}
	} 


</script>

<template>
	<div class="p-10 pb-0 flex-1 relative"> 
		<div class="border-b border-gray-200 pb-5">
			<h3 class="text-base font-semibold leading-6 text-gray-900">Les nouveaux profils</h3>
			<p class="mt-2 max-w-4xl text-sm text-gray-500">Retrouvez les dernières personnes inscrites au sein des
				agences Harmonie.</p>
		</div>

		<ul role="list" class="divide-y divide-gray-100 ">
			<li v-for="person in people" :key="person.email" class="relative flex justify-between gap-x-6 py-5">
				<div class="flex min-w-0 gap-x-4">
					<img class="h-12 w-12 flex-none rounded-full object-cover bg-gray-50"
						@error="handleImageError"
						:src="`${uri}/storage/img/cli/${person.id_cli}.webp`" alt=""
						loading="lazy" />
					<div class="min-w-0 flex-auto">
						<p class="text-sm font-semibold leading-6 text-gray-900">
							<a :href="`clients/fiche/${person.id_cli}`">
								<span class="absolute inset-x-0 -top-px bottom-0" />
								{{ isFromAgence(person).name }}
							</a>
						</p>
						<p class="mt-1 flex text-xs leading-5 text-gray-500 line-clamp-1">
							{{ person.prof_cli }} - {{ person.dateNaiss_cli ? calculateAge(person.dateNaiss_cli) + "ans" : '' }}
						</p>
					</div>
				</div>
				<div class="flex shrink-0 items-center gap-x-4">
					<div class="hidden sm:flex sm:flex-col sm:items-end">
						<p class="text-sm leading-6 text-gray-900">{{ person.ville_cli }}</p>
						<p class="mt-1 text-xs leading-5 text-gray-500">
							{{ person.taille_cli }} cm, {{ person.poid_cli }} kg
						</p>
					</div>
					<ChevronRightIcon class="h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
				</div>
			</li>
		</ul>

		<div v-if="people" class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 sticky -bottom-8 z-20">
			<div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div>
					<p class="text-sm text-gray-700">
						<span class="font-medium">{{ 100 * (pageNumber - 1) }}</span>
						{{ ' ' }}
						à
						{{ ' ' }}
						<span class="font-medium">{{ 100 * (pageNumber) > count ?  count : 100 * (pageNumber)}}</span>
						{{ ' ' }}
						sur
						{{ ' ' }}
						<span class="font-medium">{{ count }}</span>
						{{ ' ' }}
						résultats
					</p>
				</div>
				<div>
					<nav class="isolate inline-flex -space-x-px rounded-md shadow-sm">
						<button @click="previousPage" :disabled="currentPage === 1" class="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
							<ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
						</button>

						<button v-for="(page, index) in pages" :key="index" :class="['relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0', { 'z-10 bg-rose text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-600' : page === currentPage, 'cursor-not-allowed': page === '...' }]" @click="goToPage(page)" :disabled="page === '...'">
							{{ page }}
						</button>
							
						<button @click="nextPage" :disabled="currentPage === totalPages" class="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0">
							<ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
						</button>
					</nav>
				</div>
			</div>
		</div>
	</div>
</template>
